import { useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IFilterState } from '../contexts/FilterContext';
import moment from 'moment';

/**
 * Hook to sync filter state with URL query parameters.
 * Enables shareable URLs for filter configurations.
 */
export const useFilterUrlSync = (
  filterState: IFilterState,
  setFilterState: React.Dispatch<React.SetStateAction<IFilterState>>,
  filterConfig: string[],
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef(false);
  const isUpdatingFromUrl = useRef(false);

  // Parse URL params and update filter state on mount
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const updates: Partial<IFilterState> = {};
    let hasUpdates = false;

    // Parse dates
    if (filterConfig.includes('startDate')) {
      const startDateParam = searchParams.get('startDate');
      if (startDateParam) {
        const parsed = moment(startDateParam, 'YYYY-MM-DD');
        if (parsed.isValid()) {
          updates.startDate = parsed.toDate();
          hasUpdates = true;
        }
      }
    }

    if (filterConfig.includes('endDate')) {
      const endDateParam = searchParams.get('endDate');
      if (endDateParam) {
        const parsed = moment(endDateParam, 'YYYY-MM-DD');
        if (parsed.isValid()) {
          updates.endDate = parsed.toDate();
          hasUpdates = true;
        }
      }
    }

    // Parse string filters
    const stringFilters = [
      'articleGrouping',
      'articleType',
      'viewType',
      'viewChannel',
      'viewReferrer',
      'display',
      'samGrouping',
      'lpsLeadsGroupBy',
      'hostName',
      'leadStatus',
      'vnrAggregation',
    ];

    stringFilters.forEach((filter) => {
      if (filterConfig.includes(filter)) {
        const value = searchParams.get(filter);
        if (value) {
          (updates as any)[filter] = value;
          hasUpdates = true;
        }
      }
    });

    // Parse number filters
    const numberFilters = ['leadValue', 'leadPrice'];
    numberFilters.forEach((filter) => {
      if (filterConfig.includes(filter)) {
        const value = searchParams.get(filter);
        if (value && !isNaN(parseFloat(value))) {
          (updates as any)[filter] = parseFloat(value);
          hasUpdates = true;
        }
      }
    });

    // Parse array filters (comma-separated)
    const arrayFilters = [
      'customers',
      'leadSource',
      'productActive',
      'materialActive',
      'network',
      'verlag',
    ];
    arrayFilters.forEach((filter) => {
      if (filterConfig.includes(filter)) {
        const value = searchParams.get(filter);
        if (value) {
          const parsed = value.split(',').filter(Boolean);
          if (parsed.length > 0) {
            // productActive and materialActive need number conversion
            if (filter === 'productActive' || filter === 'materialActive') {
              (updates as any)[filter] = parsed.map(Number);
            } else {
              (updates as any)[filter] = parsed;
            }
            hasUpdates = true;
          }
        }
      }
    });

    if (hasUpdates) {
      isUpdatingFromUrl.current = true;
      setFilterState((prev) => ({ ...prev, ...updates }));
      // Reset flag after a short delay to allow state update to complete
      setTimeout(() => {
        isUpdatingFromUrl.current = false;
      }, 100);
    }
  }, []); // Only run on mount

  // Update URL when filter state changes
  const updateUrl = useCallback(() => {
    if (isUpdatingFromUrl.current) return;

    // Skip URL updates if no filters are configured (preserve existing URL params)
    if (filterConfig.length === 0) return;

    // Start from current URL params to preserve any non-filter keys (e.g.
    // page-local state like `tab=campaigns`, toggles like `include_non_openers=0`).
    // Only clear + rewrite the keys this hook owns.
    const newParams = new URLSearchParams(searchParams);

    // Known filter keys managed by this hook — clear them upfront so defaults
    // drop out of the URL.
    const MANAGED_KEYS = [
      'startDate',
      'endDate',
      'articleGrouping',
      'articleType',
      'viewType',
      'viewChannel',
      'viewReferrer',
      'display',
      'samGrouping',
      'lpsLeadsGroupBy',
      'hostName',
      'leadStatus',
      'vnrAggregation',
      'leadValue',
      'leadPrice',
      'customers',
      'leadSource',
      'productActive',
      'materialActive',
      'network',
      'verlag',
    ];
    MANAGED_KEYS.forEach((k) => newParams.delete(k));

    // Handle dates
    if (filterConfig.includes('startDate') && filterState.startDate) {
      newParams.set('startDate', moment(filterState.startDate).format('YYYY-MM-DD'));
    }
    if (filterConfig.includes('endDate') && filterState.endDate) {
      newParams.set('endDate', moment(filterState.endDate).format('YYYY-MM-DD'));
    }

    // Handle string filters (only add if not default)
    const stringFilterDefaults: Record<string, string> = {
      articleGrouping: 'author',
      articleType: 'all',
      viewType: 'ft',
      viewChannel: 'all',
      viewReferrer: 'all',
      display: 'daily',
      samGrouping: 'send_type',
      lpsLeadsGroupBy: 'lead_status',
      hostName: 'finanztrends.de',
      leadStatus: 'all',
      vnrAggregation: 'auto',
    };

    Object.entries(stringFilterDefaults).forEach(([filter, defaultValue]) => {
      if (filterConfig.includes(filter)) {
        const currentValue = (filterState as any)[filter];
        if (currentValue && currentValue !== defaultValue) {
          newParams.set(filter, currentValue);
        }
      }
    });

    // Handle number filters (only add if not default)
    const numberFilterDefaults: Record<string, number> = {
      leadValue: 8,
      leadPrice: 12.5,
    };

    Object.entries(numberFilterDefaults).forEach(([filter, defaultValue]) => {
      if (filterConfig.includes(filter)) {
        const currentValue = (filterState as any)[filter];
        if (currentValue !== undefined && currentValue !== defaultValue) {
          newParams.set(filter, String(currentValue));
        }
      }
    });

    // Handle array filters (only add if not empty/all)
    const arrayFilters = [
      'customers',
      'leadSource',
      'productActive',
      'materialActive',
      'network',
      'verlag',
    ];
    arrayFilters.forEach((filter) => {
      if (filterConfig.includes(filter)) {
        const value = (filterState as any)[filter];
        if (Array.isArray(value) && value.length > 0) {
          // Skip if it's just ["all"] for leadSource
          if (filter === 'leadSource' && value.length === 1 && value[0] === 'all') {
            return;
          }
          newParams.set(filter, value.join(','));
        }
      }
    });

    // Update URL without causing navigation
    setSearchParams(newParams, { replace: true });
  }, [filterState, filterConfig, setSearchParams, searchParams]);

  // Debounced URL update
  useEffect(() => {
    if (!isInitialized.current) return;

    const timeoutId = setTimeout(updateUrl, 300);
    return () => clearTimeout(timeoutId);
  }, [updateUrl]);
};
